import React, { useEffect, useState, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

//import { useAuth } from '../../components/Providers/Auth/AuthContext';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { AlertCircle, X, Clock } from 'lucide-react';
import { getSites } from '../../../services/sitesService';
import apiService from '../../../services/apiService';
import StandardDatePicker from '../../../components/DatePicker/DatePicker';

type UserType = {
  id: number;
  code: string;
  title: string;
  created_at: string;
  updated_at: string;
};

type User = {
  id: number;
  name: string;
  role: string;
  avatar?: string;
  profile_photo?: string | null;
  user_type: UserType;
};

type Props = {
  onClose: () => void;
  onSave: (data: any) => void;
  shiftId?: number;
  isEdit?: boolean;
};

type Attendant = {
  id: number;
  name: string;
  role: string;
  avatar: string;
};

type Site = {
  id: number;
  name: string;
};
// Inline Firefox check — no external file needed
const pad = (n: number) => String(n).padStart(2, '0');

// Inline dropdown time picker — used only for Firefox
// Application-controlled 24-hour picker used consistently across browsers.
type TimeDropdownProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (value: string) => void;
  initialValue?: string;
  minTime?: string;
};

const TimeDropdown: React.FC<TimeDropdownProps> = ({ isOpen, onClose, onSelect, initialValue, minTime }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeHourRef = useRef<HTMLButtonElement>(null);
  const activeMinuteRef = useRef<HTMLButtonElement>(null);

  // Close when clicking outside the dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Scroll the currently selected hour/minute into view when opened
  useEffect(() => {
    if (!isOpen) return;
    activeHourRef.current?.scrollIntoView({ block: 'center' });
    activeMinuteRef.current?.scrollIntoView({ block: 'center' });
  }, [isOpen]);

  if (!isOpen) return null;

  const now = new Date();
  const [currentHour, currentMinute] = (initialValue || `${pad(now.getHours())}:${pad(now.getMinutes())}`).split(':');

  const hours = Array.from({ length: 24 }, (_, h) => pad(h));
  const minutes = Array.from({ length: 60 }, (_, m) => pad(m));

  // An hour is fully disabled only if every minute within it falls before minTime
  const isHourDisabled = (h: string) => (minTime ? `${h}:59` < minTime : false);
  // A minute is disabled relative to the currently selected hour
  const isMinuteDisabled = (m: string) => (minTime ? `${currentHour}:${m}` < minTime : false);

  return (
    <div
      ref={containerRef}
      className="absolute left-0 top-full mt-1 z-50 flex bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden"
    >
      <div className="w-16 max-h-56 overflow-y-auto border-r border-gray-200">
        {hours.map((h) => {
          const active = h === currentHour;
          const disabled = isHourDisabled(h);
          return (
            <button
              key={h}
              ref={active ? activeHourRef : undefined}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(`${h}:${currentMinute}`)}
              className={`block w-full text-center px-3 py-2 text-sm ${
                active ? 'bg-blue-600 text-white' : disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {h}
            </button>
          );
        })}
      </div>
      <div className="w-16 max-h-56 overflow-y-auto">
        {minutes.map((m) => {
          const active = m === currentMinute;
          const disabled = isMinuteDisabled(m);
          return (
            <button
              key={m}
              ref={active ? activeMinuteRef : undefined}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(`${currentHour}:${m}`)}
              className={`block w-full text-center px-3 py-2 text-sm ${
                active ? 'bg-blue-600 text-white' : disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {m}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ShiftModal: React.FC<Props> = ({ onClose, onSave, shiftId, isEdit = false }) => {
  // const { token } = useAuth();

  const [apiError, setApiError] = useState('');
  const [showError, setShowError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dateRef = useRef<HTMLInputElement>(null);

  const [startClockOpen, setStartClockOpen] = useState(false);
  const [endClockOpen, setEndClockOpen] = useState(false);

  const [unitLoadLimitReached, setUnitLoadLimitReached] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];

  const handleDelete = (id: number) => {
    const updated = attendants.filter((a) => a.id !== id);
    setAttendants(updated);

    if (updated.length === 0) {
      setAttendantError('At least one Shift Attendant & Assistant is required');
    }
  };

  const stripHtml = (html: string | undefined | null) => {
    if (!html) return '';
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Yup schema for validation
  const validationSchema = Yup.object().shape({
    siteId: Yup.string().required('Site is required'),
    date: Yup.string()
      .required('Date is required')
      //  Reject past dates and invalid years
      .test('min-date', 'Date must be today or a future date', (value) => {
        if (!value) return false;
        const selected = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selected >= today && selected.getFullYear() >= new Date().getFullYear();
      }),
    startTime: Yup.string().required('Start time is required'),
    endTime: Yup.string().required('End time is required'),
    description: Yup.string()
      .test('description-required', 'Description is required', (value) => {
        return stripHtml(value).length > 0;
      })
      .max(500, 'Description cannot exceed 500 characters'),

    unitOnLoad: Yup.string()
      .required('Unit Load is required and cannot be left blank.')
      .test('not-blank', 'Unit Load is required and cannot be left blank.', (value) => {
        return !!value && value.trim().length > 0;
      }),
  });

  // Initial form values
  const [initialValues, setInitialValues] = useState({
    siteId: '',
    date: '',
    startTime: '',
    endTime: '',
    description: '',
    unitOnLoad: '',
  });

  // new state for selected dropdown
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [sites, setSites] = useState<Site[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);

  const [attendants, setAttendants] = useState<Attendant[]>([]);
  const [attendantError, setAttendantError] = useState('');

  const handleAddAttendant = () => {
    if (!selectedUser) return;

    const user = users.find((u: any) => u.id === parseInt(selectedUser));
    if (user) {
      setAttendants((prev) => [
        ...prev,
        {
          id: user.id,
          name: user.name,
          role: user.user_type.title,
          avatar: user.profile_photo || `https://i.pravatar.cc/40?u=${user.id}`,
        },
      ]);
      setSelectedUser('');
      setAttendantError('');
    }
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isEdit || !shiftId) return;

    const fetchShift = async () => {
      try {
        const response = await apiService.get(`/shift-logs/${shiftId}`);

        const shift = response.data.data;

        setInitialValues({
          siteId: shift.site_id ? String(shift.site_id) : shift.site?.id ? String(shift.site.id) : '',
          date: shift.date || '',
          startTime: shift.start_time?.slice(0, 5) || '',
          endTime: shift.end_time?.slice(0, 5) || '',
          description: shift.description || '',
          unitOnLoad: shift.unit_on_load || '',
        });

        setAttendants(
          shift.attendees.map((att: any) => ({
            id: att.user.id,
            name: att.user.name,
            role: att.user.role,
            avatar: `https://i.pravatar.cc/40?u=${att.user.id}`,
          })),
        );
      } catch (err) {
        console.error(err);
      }
    };

    fetchShift();
  }, [isEdit, shiftId]);

  const fetchUsers = async () => {
    try {
      const response = await apiService.get('/users', {
        params: {
          'filter[user_type_id]': '20,21',
        },
      });
      const data = response.data;
      setUsers(data?.data?.data || data?.data || []);
    } catch (err: any) {
      console.error(err);
      const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load users';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await getSites();
        setSites(Array.isArray(response?.data) ? response.data : []);
      } catch (err: any) {
        console.error('Failed to fetch sites:', err);
        const message = err?.response?.data?.message || err?.message || 'Failed to load sites';
        setApiError(message);
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      } finally {
        setSitesLoading(false);
      }
    };

    fetchSites();
  }, []);

  // Handle form submission
  const handleSubmit = async (values: any) => {
    if (attendants.length === 0) {
      setAttendantError('At least one Shift Attendant & Assistant is required');
      return;
    }

    setAttendantError('');
    const { siteId, date, startTime, endTime, description, unitOnLoad } = values;
    // Format Dayjs or string → HH:mm:ss
    const formatTime = (time: any) => {
      if (!time) return '';
      if (dayjs.isDayjs(time)) {
        return time.format('HH:mm:ss');
      }
      if (typeof time === 'string' && /^\d{2}:\d{2}$/.test(time)) {
        return `${time}:00`;
      }
      return time;
    };

    const payload = {
      site_id: Number(siteId),
      date,
      start_time: formatTime(startTime),
      end_time: formatTime(endTime),
      description,
      unit_on_load: unitOnLoad,
      attendee_user_ids: attendants.map((a) => a.id),
    };

    try {
      setIsSubmitting(true);
      const response = isEdit ? await apiService.put(`/shift-logs/${shiftId}`, payload) : await apiService.post('/shift-logs', payload);
      if (response && response.data) {
        onSave(response.data);
        onClose();
      }
    } catch (error: any) {
      console.error('Error creating shift log:', error);
      const message =
        error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to create shift. Please try again.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
      <div className="bg-white w-[90vw] max-w-5xl rounded-lg shadow-lg p-6 relative max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Add Shift</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
            &times;
          </button>
        </div>

        <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values, setFieldValue }) => (
            <Form>
              {/* Date & Time Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Site <span className="text-red-500">*</span>
                  </label>
                  <Field
                    as="select"
                    name="siteId"
                    disabled={sitesLoading}
                    className={`w-full border border-gray-300 bg-gray-100 rounded p-2
                    ${!values.siteId ? 'text-gray-400' : 'text-gray-700'}
                    outline-none focus:outline-none focus:ring-0 focus:border-gray-300 disabled:cursor-not-allowed`}
                  >
                    <option value="">{sitesLoading ? 'Loading sites...' : 'Select Site'}</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="siteId" component="div" className="text-red-500 text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Date <span className="text-red-500">*</span>{' '}
                  </label>
                  <Field name="date">
                    {({ field }: any) => (
                      <StandardDatePicker
                        inputRef={dateRef}
                        value={field.value}
                        min={todayStr}
                        onChange={(value) => setFieldValue('date', value)}
                      />
                    )}
                  </Field>
                  <ErrorMessage name="date" component="div" className="text-red-500 text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Start Time <span className="text-red-500">*</span>{' '}
                  </label>

                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setStartClockOpen((open) => !open)}
                      className={`relative w-full text-left border border-gray-300 bg-gray-100 rounded p-2 pr-10
                        ${!values.startTime ? 'text-gray-400' : 'text-gray-700'}
                        outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
                    >
                      {dayjs.isDayjs(values.startTime) ? values.startTime.format('HH:mm') : values.startTime || 'HH:mm'}
                      <Clock className="absolute right-3 top-2.5 w-4 h-4 text-gray-500" />
                    </button>

                    <TimeDropdown
                      isOpen={startClockOpen}
                      onClose={() => setStartClockOpen(false)}
                      initialValue={dayjs.isDayjs(values.startTime) ? values.startTime.format('HH:mm') : values.startTime}
                      onSelect={(time) => {
                        setFieldValue('startTime', time);
                        setFieldValue('endTime', '');
                      }}
                    />
                  </div>

                  <ErrorMessage name="startTime" component="div" className="text-red-500 text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    End Time <span className="text-red-500">*</span>{' '}
                  </label>

                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setEndClockOpen((open) => !open)}
                      className={`relative w-full text-left border border-gray-300 bg-gray-100 rounded p-2 pr-10
                        ${!values.endTime ? 'text-gray-400' : 'text-gray-700'}
                        outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
                    >
                      {dayjs.isDayjs(values.endTime) ? values.endTime.format('HH:mm') : values.endTime || 'HH:mm'}
                      <Clock className="absolute right-3 top-2.5 w-4 h-4 text-gray-500" />
                    </button>

                    <TimeDropdown
                      isOpen={endClockOpen}
                      onClose={() => setEndClockOpen(false)}
                      initialValue={dayjs.isDayjs(values.endTime) ? values.endTime.format('HH:mm') : values.endTime}
                      onSelect={(time) => setFieldValue('endTime', time)}
                    />
                  </div>

                  <ErrorMessage name="endTime" component="div" className="text-red-500 text-sm" />
                </div>
              </div>

              {/* Attendants Select UI */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Shift Attendants & Assistants
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    className={`flex-1 border border-gray-300 bg-gray-100 rounded p-2  
                   ${!selectedUser ? 'text-gray-400' : 'text-gray-700'}
                   outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                  >
                    <option value="">Select Shift Attendants & Assistants</option>
                    {users
                      .filter((u) => !attendants.some((a) => a.id === u.id))
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.user_type?.title || 'Unknown Role'})
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddAttendant}
                    disabled={!selectedUser}
                    className="px-4 py-2 border-2 border-black rounded"
                  >
                    Add
                  </button>
                </div>
              </div>
              {attendantError && <div className="text-red-500 text-sm mt-1">{attendantError}</div>}

              {/* Attendants List Table */}
              <div className="mb-6 border rounded">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left px-4 py-2 border-r border-gray-300 w-20 font-medium">Sl. No.</th>
                      <th className="text-left px-4 py-2 font-medium">Shift Attendants & Assistants</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendants.map((att, index) => (
                      <tr key={att.id} className="border-t">
                        <td className="px-4 py-2 border-r border-gray-300 text-center">{index + 1}</td>
                        <td className="px-4 py-2 flex items-center gap-2">
                          <img src={att.avatar} alt="avatar" className="w-6 h-6 rounded-full" />
                          <span>{att.name}</span>
                          <span className="text-gray-500 text-sm">, {att.role}</span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button onClick={() => handleDelete(att.id)} type="button" className="text-gray-500 hover:text-red-600">
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">
                  Shift Description
                  <span className="text-red-500">*</span>
                </label>

                <div className="bg-gray-100 border border-gray-300 rounded">
                  <ReactQuill
                    value={values.description}
                    onChange={(value) => setFieldValue('description', value)}
                    placeholder="Add description here"
                    className="bg-gray-100"
                  />
                </div>

                <ErrorMessage name="description" component="div" className="text-red-500 text-sm mt-2" />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">
                  Units on Load
                  <span className="text-red-500">*</span>
                </label>
                <Field name="unitOnLoad">
                  {({ field, form }: any) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter Units on Load"
                      maxLength={10}
                      onChange={(e) => {
                        const value = e.target.value;
                        form.setFieldValue('unitOnLoad', value);
                        setUnitLoadLimitReached(value.length >= 10);
                      }}
                      className="w-full md:w-1/2 border border-gray-300 bg-gray-100 rounded p-2 outline-none focus:outline-none focus:ring-0 focus:border-gray-300"
                    />
                  )}
                </Field>
                {unitLoadLimitReached && <div className="text-red-500 text-sm mt-2">Maximum 10 characters allowed.</div>}
                <ErrorMessage name="unitOnLoad" component="div" className="text-red-500 text-sm mt-2" />
              </div>
              <hr className="my-6 border-t border-gray-300" />

              {/* Footer Buttons */}
              <div className="flex justify-end gap-4">
                <button type="button" onClick={onClose} className="px-4 py-2 border-2 border-gray rounded ">
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-blue-700 disabled:opacity-80 disabled:cursor-not-allowed min-w-[100px] flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isEdit ? (
                    'Update Shift'
                  ) : (
                    'Add Shift'
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>

        {/* Error Notification */}
        {showError && (
          <div className="fixed bottom-6 right-6 z-[100]">
            <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative animate-slide-in">
              <button
                onClick={() => setShowError(false)}
                className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold mb-1">Error</h4>
                  <p className="text-sm">{apiError}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftModal;
